// reference: https://github.com/kentcdodds/bookshelf/blob/main/src/context/auth-context.js
'use client';

import { deleteCookie } from '@/services/CookieHelper';
import { useRouter } from 'next/navigation';
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { identifyUser, clearUserIdentity } from '@/components/google-analytics/gtag';

interface ProvideAuthContext {
  authenticated: boolean;
  userName: string | null;
  setUserName: (name: string | null) => void;
  login: () => void;
  logout: () => void;
}

interface AuthProviderProps {
  children: React.ReactNode;
  initialAuthenticated: boolean;
  initialUserName?: string | null;
}

const AuthContext = createContext<ProvideAuthContext | undefined>(undefined);

function AuthProvider({
  children,
  initialAuthenticated,
  initialUserName = null,
}: AuthProviderProps) {
  const router = useRouter();

  const [authenticated, setAuthenticated] =
    useState<boolean>(initialAuthenticated);

  const [userName, setUserName] = useState<string | null>(initialUserName);

  const login = useCallback(() => {
    setAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    deleteCookie('token_access');
    deleteCookie('token_refresh');
    setAuthenticated(false);
    setUserName(null);
    router.replace('/login');
  }, [router]);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [logout]);

  useEffect(() => {
    if (!authenticated) {
      clearUserIdentity();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch('/api/accounts/me', { credentials: 'include' });
        if (!res.ok) return;
        const me = await res.json();
        if (!cancelled && me?.id != null) {
          identifyUser(me.id, me.is_staff === true);
        }
      } catch {
        // analytics identification is best-effort only
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  const value = useMemo(
    () => ({ authenticated, userName, setUserName, login, logout }),
    [authenticated, userName, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
